import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { ChevronDownIcon, GripVerticalIcon, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface DesktopTwinPanelProps {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
  className?: string;
  panelClassName?: string;
  contentClassName?: string;
  headerActions?: ReactNode;
  collapsedActions?: ReactNode;
  defaultMinimized?: boolean;
  /** Render at all viewport sizes instead of desktop only. */
  desktopOnly?: boolean;
  minimizeOnMobile?: boolean;
  ariaLabel?: string;
}

const mobileViewportQuery = [
  '(max-width: 639px)',
  '(max-width: 935px) and (orientation: landscape)',
  '(max-height: 500px)',
].join(', ');

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * Consistent desktop overlay shell for Twin controls. Panels can be collapsed
 * and moved inside the Twin viewport without interfering with the 3D canvas.
 */
export function DesktopTwinPanel({
  title,
  icon: Icon,
  children,
  className,
  panelClassName,
  contentClassName,
  headerActions,
  collapsedActions,
  defaultMinimized = false,
  desktopOnly = true,
  minimizeOnMobile = false,
  ariaLabel,
}: DesktopTwinPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [isMinimized, setIsMinimized] = useState(defaultMinimized);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!minimizeOnMobile || defaultMinimized) return;
    const mediaQuery = window.matchMedia(mobileViewportQuery);
    const minimizeForMobileViewport = () => {
      if (mediaQuery.matches) setIsMinimized(true);
    };
    minimizeForMobileViewport();
    mediaQuery.addEventListener('change', minimizeForMobileViewport);
    window.addEventListener('orientationchange', minimizeForMobileViewport);
    return () => {
      mediaQuery.removeEventListener('change', minimizeForMobileViewport);
      window.removeEventListener('orientationchange', minimizeForMobileViewport);
    };
  }, [defaultMinimized, minimizeOnMobile]);

  const beginDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !window.matchMedia('(min-width: 640px)').matches || !panelRef.current?.parentElement) return;

    const panelRect = panelRef.current.getBoundingClientRect();
    const containerRect = panelRef.current.parentElement.getBoundingClientRect();
    const minX = position.x + containerRect.left - panelRect.left;
    const maxX = position.x + containerRect.right - panelRect.right;
    const minY = position.y + containerRect.top - panelRect.top;
    const maxY = position.y + containerRect.bottom - panelRect.bottom;

    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      minX,
      maxX,
      minY,
      maxY,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const moveDrag = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    setPosition({
      x: clamp(drag.originX + event.clientX - drag.startX, drag.minX, drag.maxX),
      y: clamp(drag.originY + event.clientY - drag.startY, drag.minY, drag.maxY),
    });
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const moveWithKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = 16;
    const delta = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    }[event.key];

    if (event.key === 'Escape') {
      dragRef.current = null;
      event.currentTarget.blur();
      return;
    }
    if (!delta) return;
    event.preventDefault();
    setPosition((current) => ({ x: current.x + delta.x, y: current.y + delta.y }));
  };

  return (
    <div
      ref={panelRef}
      className={cn('absolute', desktopOnly && 'hidden sm:block', className)}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {isMinimized ? (
        <div
          className="flex items-center gap-1 rounded-xl border bg-card/95 p-1 shadow-lg backdrop-blur"
          aria-label={ariaLabel ?? title}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                className="h-9 gap-1.5 rounded-lg px-2.5"
                aria-label={`Expand ${title}`}
                aria-expanded={false}
                onClick={() => setIsMinimized(false)}
              >
                <Icon className="size-4" aria-hidden="true" />
                <span className="max-w-28 truncate text-xs">{title}</span>
                <ChevronDownIcon className="size-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={8}>Expand {title}</TooltipContent>
          </Tooltip>
          {collapsedActions}
        </div>
      ) : (
        <Card
          className={cn('gap-0 rounded-xl border bg-card/95 p-2 shadow-lg backdrop-blur transition-all duration-200 motion-reduce:transition-none', panelClassName)}
          aria-label={ariaLabel ?? title}
        >
          <div className="flex items-center gap-1 px-1 pb-1">
            <div
              className="flex min-w-0 flex-1 cursor-grab touch-none select-none items-center gap-1.5 active:cursor-grabbing"
              role="button"
              tabIndex={0}
              aria-label={`Move ${title}. Use arrow keys for small adjustments.`}
              onPointerDown={beginDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onLostPointerCapture={endDrag}
              onKeyDown={moveWithKeyboard}
            >
              <GripVerticalIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate text-xs font-semibold text-muted-foreground">{title}</span>
            </div>
            {headerActions}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 shrink-0 rounded-lg"
                  aria-label={`Collapse ${title}`}
                  aria-expanded={true}
                  onClick={() => setIsMinimized(true)}
                >
                  <ChevronDownIcon className="size-4 rotate-180" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={8}>Collapse {title}</TooltipContent>
            </Tooltip>
          </div>
          <CardContent className={cn('p-1', contentClassName)}>{children}</CardContent>
        </Card>
      )}
    </div>
  );
}
